export default function invoiceWizard(config) {
    return {
        // ━━━ State ━━━
        currentStep: 1,
        jobId: null,
        templateId: null,
        periodId: null,
        freeText: '',
        delsites: [],
        precheckData: null,
        invoiceProgress: {current: 0, total: 0},
        isLoadingDelsites: false,

        // ━━━ Config ━━━
        routes: config.routes,
        pollInterval: config.pollInterval ?? 3000,
        maxPollingErrors: 3,
        invoiceTemplates: config.invoiceTemplates ?? [],

        // ━━━ Private State ━━━
        _precheckTimer: null,
        _progressTimer: null,
        _pageShowHandler: null,
        _inFlight: false,

        // ━━━ Lifecycle ━━━
        init() {
            console.log('Invoice wizard initialized', {
                pollInterval: this.pollInterval,
                templateCount: this.invoiceTemplates.length
            });

            this._pageShowHandler = this._handlePageShow.bind(this);
            window.addEventListener('pageshow', this._pageShowHandler);
        },

        destroy() {
            this._stopAll();
            if (this._pageShowHandler) {
                window.removeEventListener('pageshow', this._pageShowHandler);
            }
        },

        _handlePageShow(event) {
            if (event.persisted) {
                console.log('Page loaded from cache, resetting state');
                this._resetState();
            }
        },

        // ━━━ Private Helpers ━━━
        _csrf() {
            return document.head.querySelector('meta[name=csrf-token]')?.content ?? '';
        },

        _stopAll() {
            if (this._precheckTimer) {
                clearInterval(this._precheckTimer);
                this._precheckTimer = null;
            }
            if (this._progressTimer) {
                clearInterval(this._progressTimer);
                this._progressTimer = null;
            }
        },

        _resetState() {
            this.currentStep = 1;
            this.jobId = null;
            this.templateId = null;
            this.periodId = null;
            this.freeText = '';
            this.delsites = [];
            this.precheckData = null;
            this.invoiceProgress = {current: 0, total: 0};
            this.isLoadingDelsites = false;
            this._inFlight = false;
            this._stopAll();
        },

        _toast(message, error = null) {
            if (error) console.error(error);
            alert(message);
        },

        route(name, params = {}) {
            const url = this.routes?.[name];
            if (!url) throw new Error(`Route "${name}" not found`);

            return Object.entries(params).reduce(
                (acc, [key, value]) => acc.replace(`:${key}`, encodeURIComponent(String(value))),
                url
            );
        },

        async _json(url, {method = 'GET', body} = {}) {
            const options = {
                method,
                headers: {
                    'Content-Type': 'application/json',
                    'X-CSRF-TOKEN': this._csrf()
                }
            };

            if (body) {
                options.body = JSON.stringify(body);
            }

            const response = await fetch(url, options);

            if (!response.ok) {
                throw new Error(`HTTP ${response.status}`);
            }

            try {
                return await response.json();
            } catch {
                return {};
            }
        },

        _poll(fn, {interval = this.pollInterval, stopIf = () => false, onDone = () => {}} = {}) {
            let errorCount = 0;

            const timerId = setInterval(async () => {
                if (document.hidden) return;
                if (this._inFlight) return;

                this._inFlight = true;
                try {
                    const data = await fn();
                    errorCount = 0;

                    if (stopIf(data)) {
                        clearInterval(timerId);
                        onDone(data);
                    }
                } catch (error) {
                    errorCount++;
                    console.error('Polling error:', error);

                    if (errorCount >= this.maxPollingErrors) {
                        clearInterval(timerId);
                        onDone({__error: error});
                    }
                } finally {
                    this._inFlight = false;
                }
            }, interval);

            return timerId;
        },

        // ━━━ Public Methods ━━━
        async selectTemplate(templateId) {
            this.templateId = templateId;
            this.periodId = null;
            await this.loadDelsites();
        },

        async loadDelsites() {
            if (!this.templateId) return;

            this.delsites = [];
            this.isLoadingDelsites = true;

            try {
                const data = await this._json(this.routes.delsitesBase + '/' + this.templateId);
                this.delsites = Array.isArray(data) ? data : Object.values(data ?? {});
            } catch (error) {
                this._toast('Kunde inte ladda medlemmar. Kontrollera din anslutning.', error);
            } finally {
                this.isLoadingDelsites = false;
            }
        },

        isValid() {
            return this.templateId != null && this.periodId != null;
        },

        async submitTemplateSelection() {
            if (!this.isValid()) return;

            this.currentStep = 2;

            try {
                const data = await this._json(this.route('createJob'), {
                    method: 'POST',
                    body: {
                        invoiceTemplateId: this.templateId,
                        periodId: this.periodId,
                        freeText: this.freeText
                    }
                });

                this.jobId = data.jobId;
                this.startPrecheckPolling();
            } catch (error) {
                this.currentStep = 1;
                this._toast('Kunde inte skapa jobb. Försök igen.', error);
            }
        },

        startPrecheckPolling() {
            if (!this.jobId) return;
            this._stopAll();

            this._precheckTimer = this._poll(
                () => this._json(this.route('precheckStatus', {jobId: this.jobId})),
                {
                    stopIf: (data) => data?.status === 'completed' || data?.__error,
                    onDone: (data) => {
                        if (data?.__error) {
                            this._toast('Kunde inte hämta status. Försök igen senare.', data.__error);
                            this.redirectToJobView();
                            return;
                        }

                        this.precheckData = data?.response ?? data;

                        if (this.hasMissingMembers) {
                            this.redirectToJobView();
                            return;
                        }

                        this.currentStep = 3;
                    }
                }
            );
        },

        async confirmAndCreateInvoices() {
            if (!this.canProceed) return;

            this.currentStep = 4;
            this.invoiceProgress = {current: 0, total: 0};

            try {
                const data = await this._json(this.route('createInvoices', {jobId: this.jobId}), {
                    method: 'POST'
                });

                if (!data?.success) {
                    this.redirectToJobView();
                    return;
                }

                this.startProgressPolling();
            } catch (error) {
                console.error('Error creating invoices:', error);
                this.redirectToJobView();
            }
        },

        startProgressPolling() {
            if (!this.jobId) return;
            if (this._progressTimer) clearInterval(this._progressTimer);

            this._progressTimer = this._poll(
                () => this._json(this.route('status', {jobId: this.jobId})),
                {
                    stopIf: (data) => data?.status === 'completed' || data?.__error,
                    onDone: async (data) => {
                        if (data?.__error) {
                            this._toast('Kunde inte hämta faktureringsstatus. Försök igen senare.', data.__error);
                            this.redirectToJobView();
                            return;
                        }

                        this.invoiceProgress = {
                            current: data.current ?? 0,
                            total: data.total ?? 0
                        };

                        console.log('Progress complete:', {
                            current: this.invoiceProgress.current,
                            total: this.invoiceProgress.total,
                            percent: this.progressPercent
                        });

                        await this.redirectToInvoices();
                    }
                }
            );
        },

        async redirectToInvoices() {
            try {
                const data = await this._json(this.route('complete', {jobId: this.jobId}), {
                    method: 'POST'
                });

                if (data?.success && data?.redirectUrl) {
                    window.location.href = data.redirectUrl;
                } else {
                    this.redirectToJobView();
                }
            } catch (error) {
                console.error('Error completing job:', error);
                this.redirectToJobView();
            }
        },

        redirectToJobView() {
            window.location.href = this.route('show', {jobId: this.jobId});
        },

        // ━━━ Computed Properties ━━━
        get missingMembers() {
            return this.precheckData?.missing?.members ?? [];
        },

        get hasMissingMembers() {
            return this.missingMembers.length > 0;
        },

        get canProceed() {
            return !this.hasMissingMembers;
        },

        get progressPercent() {
            const {current, total} = this.invoiceProgress;
            if (!total) return 0;
            return Math.max(0, Math.min(100, Math.round((current / total) * 100)));
        },

        get precheckTypes() {
            return this.precheckData?.consumptionSum?.types ?? [];
        },

        get precheckConsumption() {
            return this.precheckData?.consumptionSum?.consumption ?? '';
        },

        get hasPrecheckData() {
            return this.precheckData !== null;
        }
    };
}
